<template>
  <TheNavbar />

  <div class="mt-5 mb-5">

    <div class="row justify-content-center mb-3">
      <div class="col-sm-12 col-md-10 col-lg-8 col-xl-8 col-xxl-6">

        <div class="text-center mb-4">
          <h3>Latest Updates</h3>
          <p>What we've launched and fixed recently - and what's coming next.</p>
          <p class="mb-0">Questions? Come to our 💬 <a :href="getSupportUrl()" target="_blank">Telegram Group <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span></a> where we help thousands of users 24/7 with admins from all over the world.</p>
        </div>

        <!-- Coming soon: IBKR -->
        <div id="ibkr-integration" class="release mb-4" :class="{ 'release-highlight': releaseId === 'ibkr-integration' }">
          <div class="card">
            <div class="card-header">
              <div class="release-head">
                <h5 class="mb-0 release-head-title" data-anchor="data-anchor">IBKR Integration - Trade Stocks with AI</h5>
                <CopyReleaseLinkButton class="ms-2 flex-shrink-0" release-id="ibkr-integration" />
              </div>
            </div>
            <div class="card-body bg-body-tertiary">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <span class="badge badge-subtle-info rounded-pill">Coming soon</span>
                <span class="text-body-tertiary fs-10">August 2026</span>
              </div>
              <img class="img-fluid rounded mx-auto d-block mb-3" :src="require('@/assets/updates/ibkr_upcoming.jpg')" alt="IBKR integration coming soon to SIGNUM" />
              <p class="mb-0">
                We are working on an Interactive Brokers (IBKR) integration, so you can trade <b>Stocks</b> with AI via SIGNUM - the same way you already trade crypto today.
              </p>
            </div>
          </div>
        </div>

        <!-- Read-line: everything above is new since the user's last visit -->
        <div v-if="showReadLineAfter('ibkr-integration')" class="read-line mb-4"> 
          <span><span class="fas fa-arrow-up me-1"></span>New since your last visit</span>
        </div>

        <!-- Yield-earning stablecoins (USDG / USDGO) -->
        <div id="stablecoin-yield" class="release mb-4" :class="{ 'release-highlight': releaseId === 'stablecoin-yield' }">
          <div class="card">
            <div class="card-header">
              <div class="release-head">
                <h5 class="mb-0 release-head-title" data-anchor="data-anchor">Earn Yield on Idle Stablecoins (3.5% - 4.1%)</h5>
                <CopyReleaseLinkButton class="ms-2 flex-shrink-0" release-id="stablecoin-yield" />
              </div>
            </div>
            <div class="card-body bg-body-tertiary">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <span class="badge badge-subtle-success rounded-pill">Launched</span>
                <span class="text-body-tertiary fs-10">2nd August 2026</span>
              </div>
              <img class="img-fluid rounded mx-auto d-block mb-3" :src="require('@/assets/updates/earn_yield_on_stablecoins.jpg')" alt="Routine editor: the auto-yield snippet pasted just above the REVIEW step of a Long-Only strategy prompt" />
              <p>
                Your <router-link :to="{name: 'Strategies'}">AI Trading Strategies</router-link> can now put your idle stablecoins to work, so your cash keeps earning even when you are between trades.
              </p>
              <p>
                When a Long-Only strategy is not fully invested, it usually parks the rest in plain stablecoins (like USDC or USDT) that just sit there doing nothing.<br>
                <br>
                Now the AI can move that unused balance into a <b>yield-earning stablecoin</b> instead - so you earn a hold-to-earn yield while you wait for the next entry, and it trades straight back out when it is time to buy.<br>
                <br>
                You asked us how to make your idle cash work harder between trades. Here it is - USDG and USDGO earn yield just by holding them, no staking and no lock-ups.
              </p>
              <p class="mb-2">Two yield-earning stablecoins are supported today:</p>
              <ul>
                <li><b>USDG</b> on <b>OKX</b> (EU &amp; World) - earns around <b>3.5% - 4.1%</b> per year. <a href="https://www.okx.com/learn/usdg" target="_blank">Learn more <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span></a></li>
                <li><b>USDGO</b> on <b>Bitget</b> - earns around <b>4%</b> per year. <a href="https://www.bitget.com/academy/what-is-usdgo-the-enterprise-stablecoin" target="_blank">Learn more <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span></a></li>
              </ul>
              <p class="mb-0">
                This makes the biggest difference for <router-link :to="{name: 'Strategies'}">Long-Only strategies</router-link>, which spend longer stretches out of the market - that idle time now earns yield instead of nothing.
              </p>

              <hr class="my-3">
              <p class="mb-2">
                <b>How to use it?</b><br>
                Turn this on in seconds: add the snippet to your prompt, <b>just above the REVIEW step</b> (as shown in the image above). Copy and paste it into your prompt:
              </p>
              <p class="fw-bold fs-9 mb-1"><span class="fas fa-arrow-right me-1 text-primary"></span>OKX Spot bot - earn USDG:</p>
              <CodeBlock class="mb-1" wrap :code="okxYieldSnippet" />
              <p class="fs-9 mb-0">
                <span class="fas fa-tag text-primary me-1"></span>No OKX account yet? <a href="https://www.okx.com/join/SIGNUM" target="_blank">Sign up with our link <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span></a> and save <b>15%</b> on trading fees.
              </p>
              <p class="fw-bold fs-9 mb-1 mt-3"><span class="fas fa-arrow-right me-1 text-primary"></span>Bitget Spot bot - earn USDGO:</p>
              <CodeBlock class="mb-1" wrap :code="bitgetYieldSnippet" />
              <p class="fs-9 mb-0">
                <span class="fas fa-tag text-primary me-1"></span>No Bitget account yet? <a href="https://partner.bitget.com/bg/SIGNUM1" target="_blank">Sign up with our link <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span></a> and save <b>15%</b> on trading fees.
              </p>
              <p class="mt-3">Yes, the AI knows how to convert back to what it needs to trade again 🫡</p>
            </div>
          </div>
        </div>

        <!-- Read-line: everything above is new since the user's last visit -->
        <div v-if="showReadLineAfter('stablecoin-yield')" class="read-line mb-4">
          <span><span class="fas fa-arrow-up me-1"></span>New since your last visit</span>
        </div>

        <!-- Hyperliquid API key validation -->
        <div id="hyperliquid-key-validation" class="release mb-4" :class="{ 'release-highlight': releaseId === 'hyperliquid-key-validation' }">
          <div class="card">
            <div class="card-header">
              <div class="release-head">
                <h5 class="mb-0 release-head-title" data-anchor="data-anchor">Hyperliquid API Key Validation</h5>
                <CopyReleaseLinkButton class="ms-2 flex-shrink-0" release-id="hyperliquid-key-validation" />
              </div>
            </div>
            <div class="card-body bg-body-tertiary">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <span class="badge badge-subtle-success rounded-pill">Launched</span>
                <span class="text-body-tertiary fs-10">19th July 2026</span>
              </div>
              <img class="img-fluid rounded mx-auto d-block mb-3" :src="require('@/assets/updates/hyperliquid_api_key_validation_launch.jpg')" alt="Create New Bot: entering and validating the Hyperliquid API wallet address, private key and account address" />
              <p class="mb-0">
                Setting up a <b>Hyperliquid</b> Bot now tells you right away when something is wrong with your API keys 🤩<br>
                <br>
                We check that your API wallet address and your private key belong together, that the API wallet is approved for your account, and that it is not expired - all before the Bot is created. When something doesn't match, you get a clear message about what to fix, instead of finding out later when a signal doesn't go through.<br>
                <br>
                Hyperliquid offers no way to look up API keys, but we found a way to validate your keys to make SIGNUM even better for you ❤️<br>
                <br>
                <router-link :to="{name: 'CreateNewBot'}">Create a Bot</router-link> to get started.
              </p>
            </div>
          </div>
        </div>

        <!-- Read-line: everything above is new since the user's last visit -->
        <div v-if="showReadLineAfter('hyperliquid-key-validation')" class="read-line mb-4">
          <span><span class="fas fa-arrow-up me-1"></span>New since your last visit</span>
        </div>

        <!-- OKX EU Futures -->
        <div id="okx-eu-futures" class="release mb-4" :class="{ 'release-highlight': releaseId === 'okx-eu-futures' }"> 
          <div class="card">
            <div class="card-header">
              <div class="release-head">
                <h5 class="mb-0 release-head-title" data-anchor="data-anchor">OKX EU Futures Support</h5>
                <CopyReleaseLinkButton class="ms-2 flex-shrink-0" release-id="okx-eu-futures" />
              </div>
            </div>
            <div class="card-body bg-body-tertiary">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <span class="badge badge-subtle-success rounded-pill">Launched</span>
                <span class="text-body-tertiary fs-10">15th July 2026</span>
              </div>
              <img class="img-fluid rounded mx-auto d-block mb-3" :src="require('@/assets/updates/okxeea_futures_launch.jpg')" alt="Create New Bot: select the Futures account type and the OKX EU exchange" />
              <p>
                You can now trade Futures on <b>OKX EU</b> with your SIGNUM Bots.<br>
                <br>
                We built this so our EU users can trade Perps on a <b>MiCA-regulated</b> exchange - fully compliant, no compromises. We love our users all around the world and work tirelessly so you can always trade everything you want and need, wherever you are.<br>
                <br>
                <router-link :to="{name: 'CreateNewBot'}">Create a Bot</router-link> to get started.
              </p>
              <div class="alert alert-warning border-0 d-flex align-items-center mb-0" role="alert">
                <div class="bg-warning me-3 icon-item"><span class="fas fa-exclamation-circle text-white fs-6"></span></div>
                <p class="mb-0 flex-1">
                  <span class="fw-bold">Update:</span> For some users, when activating X-Perps, OKX moves all assets to the <b>Funding</b> account. Move them to the <b>Trading</b> account - otherwise you can't trade with those funds and SIGNUM won't see them. Takes a few seconds.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Read-line: everything above is new since the user's last visit -->
        <div v-if="showReadLineAfter('okx-eu-futures')" class="read-line mb-4">
          <span><span class="fas fa-arrow-up me-1"></span>New since your last visit</span>
        </div>

        <!-- API Keys -->
        <div id="api-keys" class="release mb-4" :class="{ 'release-highlight': releaseId === 'api-keys' }">
          <div class="card">
            <div class="card-header">
              <div class="release-head">
                <h5 class="mb-0 release-head-title" data-anchor="data-anchor">API Keys - Build Your Own SIGNUM Integrations</h5>
                <CopyReleaseLinkButton class="ms-2 flex-shrink-0" release-id="api-keys" />
              </div>
            </div>
            <div class="card-body bg-body-tertiary">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <span class="badge badge-subtle-success rounded-pill">Launched</span>
                <span class="text-body-tertiary fs-10">26th June 2026</span>
              </div>
              <img class="img-fluid rounded mx-auto d-block mb-3" :src="require('@/assets/updates/api_keys_launched.jpg')" alt="Create an API Key: choose a label and the Read, Write and Trading permissions" />
              <p>
                You can now create your own <b>API Keys</b> and use SIGNUM programmatically - from your own scripts, tools and dashboards.
              </p>
              <p>
                Many of you, especially the developers among you, asked us for a clean way to build on top of SIGNUM. We heard you and we built it, because giving you full control over your own trading is what SIGNUM is all about.
              </p>
              <p>
                Every key is <b>scoped</b>, so it only gets the permissions you choose:
              </p>
              <ul>
                <li><b>Read</b> - read your resources (always granted).</li>
                <li><b>Write</b> - create and modify your Bots and settings.</li>
                <li><b>Trading</b> - execute trades on your Bots.</li>
              </ul>
              <p class="mb-0">
                Head over to the <router-link :to="{name: 'ApiKeys'}">API Keys</router-link> page to create your first key - you will find the API documentation there as well.
              </p>
            </div>
          </div>
        </div>

        <!-- Read-line: everything above is new since the user's last visit -->
        <div v-if="showReadLineAfter('api-keys')" class="read-line mb-4">
          <span><span class="fas fa-arrow-up me-1"></span>New since your last visit</span>
        </div>

        <!-- SIGNUM MCP & AI Trading Strategies -->
        <div id="mcp-ai-strategies" class="release mb-4" :class="{ 'release-highlight': releaseId === 'mcp-ai-strategies' }">
          <div class="card">
            <div class="card-header">
              <div class="release-head">
                <h5 class="mb-0 release-head-title" data-anchor="data-anchor">SIGNUM MCP &amp; AI Trading Strategies</h5>
                <CopyReleaseLinkButton class="ms-2 flex-shrink-0" release-id="mcp-ai-strategies" />
              </div>
            </div>
            <div class="card-body bg-body-tertiary">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <span class="badge badge-subtle-success rounded-pill">Launched</span>
                <span class="text-body-tertiary fs-10">14th June 2026</span>
              </div>
              <vue-image-zoomer img-class="img-fluid rounded mx-auto d-block mb-3" :click-zoom="true" :zoom-amount="2" :show-message-touch="false" :tap-to-close="true" :regular="require('@/assets/updates/mcp_launch.jpg')" :zoom="require('@/assets/updates/mcp_launch.jpg')" alt="SIGNUM MCP and AI Trading Strategies" />
              <p>
                You can now drive your Bots with an AI agent (like Claude) over the <b>SIGNUM MCP</b>.<br>
                <br>
                Connect your AI agent to SIGNUM and it can:
              </p>
              <ul>
                <li>Read live market data: Trend Radar signals, pair prices and exchange info.</li>
                <li>See your Bots, their assets and their trading history.</li>
                <li>Trade for you by sending trading signals to your Bots - you stay in control of which Bot and exchange it uses.</li>
                <li>Run hands-free: wrap a strategy prompt in a scheduled Routine and your AI trades automatically.</li>
              </ul>
              <p>
                To get you started we also launched ready-made <b>AI Trading Strategies</b> on the <router-link :to="{name: 'Strategies'}">Strategies</router-link> page - pick one of your Bots, copy the prompt (it's personalized with your Bot's ID) and run it in your AI agent.
              </p>
              <p>
                <a href="https://autotrading.vip" target="_blank"><b>AutoTrading.vip</b> <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span></a> members get 4 extra strategies (3 crypto and 1 stocks) that outperform the public Gaussian Channel ones.
              </p>
              <p class="mb-2">Watch these videos to learn how to use it:</p>
              <ul class="list-unstyled ms-1">
                <li class="mb-1">
                  <a href="https://www.youtube.com/watch?v=6w3vaAUT3RE" target="_blank" title="Watch the SIGNUM MCP walkthrough"><span class="fab fa-youtube me-2"></span><span class="fw-bold">Automate Your Portfolio with Claude Routines - SIGNUM</span> <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span></a>
                </li>
                <li>
                  <a href="https://www.youtube.com/watch?v=fdGCGXcDByk" target="_blank" title="Watch the AI Trading Strategies walkthrough"><span class="fab fa-youtube me-2"></span><span class="fw-bold">Full Portfolio Automation with AI - Michael Automates</span> <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span></a>
                </li>
              </ul>
              <p class="mb-0">
                <span class="fas fa-lightbulb text-warning me-1"></span>Tip: The best AI model to use is <b>Sonnet 5</b>.
              </p>
            </div>
          </div>
        </div>

        <!-- Read-line: everything above is new since the user's last visit -->
        <div v-if="showReadLineAfter('mcp-ai-strategies')" class="read-line mb-4">
          <span><span class="fas fa-arrow-up me-1"></span>New since your last visit</span>
        </div>

        <!-- Trend Radar -->
        <div id="trend-radar" class="release mb-4" :class="{ 'release-highlight': releaseId === 'trend-radar' }">
          <div class="card">
            <div class="card-header">
              <div class="release-head">
                <h5 class="mb-0 release-head-title" data-anchor="data-anchor">Trend Radar - Daily Trend Signals</h5>
                <CopyReleaseLinkButton class="ms-2 flex-shrink-0" release-id="trend-radar" />
              </div>
            </div>
            <div class="card-body bg-body-tertiary">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <span class="badge badge-subtle-success rounded-pill">Launched</span>
                <span class="text-body-tertiary fs-10">7th April 2026</span>
              </div>
              <vue-image-zoomer img-class="img-fluid rounded mx-auto d-block mb-3" :click-zoom="true" :zoom-amount="2" :show-message-touch="false" :tap-to-close="true" :regular="require('@/assets/updates/trend_radar_launch.jpg')" :zoom="require('@/assets/updates/trend_radar_launch.jpg')" alt="Trend Radar table with daily trend flips and a chart preview of a Red to Green flip with a bullish breakout" />
              <p>
                We launched the <b>Trend Radar</b> - your daily overview of which assets are trending up, moving sideways or trending down.
              </p>
              <p>
                One of the questions you ask us the most is "What should I actually trade?". The Trend Radar is our answer.<br>
                <br>
                Every day it scans the markets and shows you every trend flip - so you spot opportunities early, without staring at charts all day.
              </p>
              <p>
                It comes with 2 Trend Indicators:
              </p>
              <ul>
                <li><b>GaussianChannel</b> - available to all SIGNUM users. It scans the <b>Crypto</b> market for now (Stocks coming soon).</li>
                <li><b>Bot2Slow</b> <span class="fas fa-crown" title="AutoTrading VIP only"></span> - scans all markets (<b>Crypto</b>, <b>Stocks</b>, <b>Metals</b> and <b>Commodities</b>). Exclusively available to <a href="https://autotrading.vip" target="_blank"><b>AutoTrading.vip</b> <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span></a> members.</li>
              </ul>
              <p>
                See today's trends on the <router-link :to="{name: 'TrendRadar'}">Trend Radar</router-link> page.
              </p>
              <p class="mb-0">
                <span class="fas fa-lightbulb text-warning me-1"></span>Fun fact: the Trend Radar data also powers our <router-link :to="{name: 'Strategies'}">AI Trading Strategies</router-link> today.
              </p>
            </div>
          </div>
        </div>

        <!-- Read-line: everything above is new since the user's last visit -->
        <div v-if="showReadLineAfter('trend-radar')" class="read-line mb-4">
          <span><span class="fas fa-arrow-up me-1"></span>New since your last visit</span>
        </div>

        <!-- Bybit EU -->
        <div id="bybit-eu" class="release mb-4" :class="{ 'release-highlight': releaseId === 'bybit-eu' }">
          <div class="card">
            <div class="card-header">
              <div class="release-head">
                <h5 class="mb-0 release-head-title" data-anchor="data-anchor">Bybit EU Support</h5>
                <CopyReleaseLinkButton class="ms-2 flex-shrink-0" release-id="bybit-eu" />
              </div>
            </div>
            <div class="card-body bg-body-tertiary">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <span class="badge badge-subtle-success rounded-pill">Launched</span>
                <span class="text-body-tertiary fs-10">7th March 2026</span>
              </div>
              <img class="img-fluid rounded mx-auto d-block mb-3" :src="require('@/assets/updates/bybiteea_launch.jpg')" alt="Create New Bot: select the Spot account type and the Bybit EU exchange" />
              <p>
                You can now trade Spot on <b>Bybit EU</b> (bybit.eu) with your SIGNUM Bots.
              </p>
              <p>
                When MiCA regulation arrived in Europe, many of our EU users asked us for a compliant way to keep trading on Bybit. So we integrated Bybit's <b>MiCA-regulated</b> EU platform.<br>
                <br>
                You matter to us, wherever you trade from - and we want you to trade safely and compliantly.
              </p>
              <p class="mb-0">
                <router-link :to="{name: 'CreateNewBot'}">Create a Bot</router-link> and select Bybit EU to get started.
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import TheNavbar from '@/components/TheNavbar'
import CopyReleaseLinkButton from '@/components/CopyReleaseLinkButton'
import CodeBlock from '@/components/CodeBlock'
import { UPDATES } from '@/utils/updates'

export default {
  components: {
    TheNavbar,
    CopyReleaseLinkButton,
    CodeBlock
  },
  // This page is intentionally PUBLIC (no login redirect) so update links
  // can be shared with anyone, e.g. in the community or on social media.
  props: {
    releaseId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      updates: UPDATES,
      // The last-visit timestamp captured BEFORE this visit marks itself read
      // (see created/mounted). Used to draw the "read up to here" line. Read
      // straight from localStorage to avoid racing App.vue's async store init.
      readLineAt: null,
      okxYieldSnippet: [
        '4.1) If this is an OKX Spot bot:',
        'Trade any unused/remaining stablecoin/fiat balance into USDG to earn the hold-to-earn yield while holding this stablecoin.'
      ].join('\n'),
      bitgetYieldSnippet: [
        '4.1) If this is a Bitget Spot bot:',
        'Trade any unused/remaining stablecoin/fiat balance into USDGO to earn the hold-to-earn yield while holding this stablecoin.'
      ].join('\n')
    }
  },
  methods: {
    // Same Telegram support link as TheHelpChatbot / ExchangeAddFundsInfo
    getSupportUrl () {
      return process.env.VUE_APP_SUPPORT_URL
    },
    // A release is unread if it was published after the user's previous visit
    // (everything is unread if they never visited before).
    isUnread (publishedAt) {
      if (!this.readLineAt) return true
      return new Date(publishedAt).getTime() > new Date(this.readLineAt).getTime()
    },
    // Draw the divider directly below `id` when it's the last unread release,
    // i.e. this one is unread but the next (older) one is already read.
    showReadLineAfter (id) {
      const idx = this.updates.findIndex(u => u.id === id)
      const next = this.updates[idx + 1]
      if (idx === -1 || !next) return false
      return this.isUnread(this.updates[idx].publishedAt) && !this.isUnread(next.publishedAt)
    },
    // The read time we persist: bare "now". No cushion needed since we mark read
    // on LEAVE — this is always later than the view time, and thus later than any
    // already-published update the user just read.
    updatesReadTimestamp () {
      return new Date().toISOString()
    },
    // Tab close / reload / external navigation: beforeUnmount does NOT run, so
    // persist straight to localStorage (the store may already be tearing down).
    persistUpdatesReadOnHide () {
      localStorage.setItem('updatesLastReadAt', this.updatesReadTimestamp())
    }
  },
  created () {
    // Capture the PREVIOUS visit time before we mark this visit read on leave.
    this.readLineAt = localStorage.getItem('updatesLastReadAt') || null
  },
  mounted () {
    // NOTE: we intentionally do NOT mark updates read on mount. The badge should
    // stay visible the whole time the user is on /updates and only clear when
    // they LEAVE (see beforeUnmount) — otherwise it vanishes the instant they
    // open the page. `pagehide` covers the leave paths beforeUnmount misses
    // (tab close, reload, navigating to an external URL).
    window.addEventListener('pagehide', this.persistUpdatesReadOnHide)

    // In-app navigations are scrolled by the router's scrollBehavior (see
    // router/index.js). This covers the initial page load, where the component
    // can mount AFTER that scroll already ran (App.vue's readyToRender gate).
    if (!this.releaseId) return
    this.$nextTick(() => {
      document.getElementById(this.releaseId)?.scrollIntoView({ behavior: 'instant', block: 'start' })
    })
  },
  beforeUnmount () {
    // Mark read when the user leaves /updates via in-app (SPA) navigation. This
    // reactively clears the navbar badge + avatar dot on the next page. Note a
    // param-only change within /updates (e.g. /updates/<id>) reuses this
    // component and does NOT unmount, so the badge correctly stays.
    window.removeEventListener('pagehide', this.persistUpdatesReadOnHide)
    this.$store.commit('setUpdatesLastReadAt', { timestamp: this.updatesReadTimestamp() })
  }
}
</script>

<style scoped>
/* Keeps the anchored card clear of the sticky navbar when scrolled into view */
.release {
  scroll-margin-top: 5rem;
}
.release-highlight .card {
  box-shadow: 0 0 0 2px var(--falcon-primary);
}
/* Card header: badge + title on the left, date + copy-link on the right.
   Desktop = single row, vertically centered. */
.release-head {
  display: flex;
  align-items: center;
}
.release-head-title {
  flex: 1 1 auto;
  min-width: 0; /* let the title wrap instead of pushing the icon off */
}
/* Small screens: the title wraps beside the always-right link icon; keep the
   icon aligned to the title's first line rather than centered on the block. */
@media (max-width: 767.98px) {
  .release-head {
    align-items: flex-start;
  }
}
/* Boundary between unread (above) and previously-read (below) releases */
.read-line {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--falcon-danger);
}
.read-line::before,
.read-line::after {
  content: "";
  flex: 1;
  border-top: 1px solid var(--falcon-danger);
  opacity: 0.5;
}
.read-line span {
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}
</style>
